import chevronLeft from "@assets/images/chevron-left.svg";
import EOrderingLogo from "@assets/images/eOrdering.png";
import SCGPLogo from "@assets/images/SCGP-color.svg";
import { Button, TextField } from "@material-ui/core";
import FormSpacer from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import { RequestPasswordResetMutation } from "@saleor/graphql";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { commonMessages } from "@saleor/intl";
import classNames from "classnames";
import { useFormik } from "formik";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "../styles";

export interface ResetPasswordPageFormData {
  email: string;
}

export interface ResetPasswordPageProps {
  disabled: boolean;
  error: string;
  onBack: () => void;
  onSubmit: (
    data: ResetPasswordPageFormData
  ) => SubmitPromise<
    RequestPasswordResetMutation["requestPasswordReset"]["errors"]
  >;
}
const ResetPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format ")
    .test(
      "email",
      "Please input external email",
      val => val && !val.includes("@scg.com")
    )
});
const ResetPasswordPage: React.FC<ResetPasswordPageProps> = props => {
  const { disabled, error, onBack, onSubmit } = props;
  const formik = useFormik({
    initialValues: {
      email: ""
    },
    onSubmit: values => {
      onSubmit(values);
    },
    validationSchema: ResetPasswordSchema
  });
  const classes = useStyles(props);
  const intl = useIntl();

  return (
    <div className={classes.root}>
      <div className={classes.mainPanel}>
        <div className={classes.headTextLogo}>
          <div className={classes.text}>
            <img src={SCGPLogo} alt={"logo"} />
          </div>
          <div className={classes.logo}>
            <img src={EOrderingLogo} alt={"logo"} />
          </div>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <>
            <MyTypography variant="h5" className={classes.header}>
              <FormattedMessage defaultMessage="Forgot your password?" />
            </MyTypography>
            {!!error && <div className={classes.panel}>{error}</div>}
            <MyTypography
              variant="body3"
              color="textSecondary"
              className={classes.description}
            >
              <FormattedMessage defaultMessage="Please enter the email address you used to sign in and we'll send you a password reset link." />
            </MyTypography>
            <FormSpacer space={32} />
            <TextField
              autoFocus
              disabled={disabled}
              fullWidth
              autoComplete="username"
              label={intl.formatMessage(commonMessages.emailOnly)}
              name="email"
              onChange={formik.handleChange}
              value={formik.values.email}
              error={formik.errors.email && formik.touched.email}
              inputProps={{
                "data-testid": "email"
              }}
            />
            <div className={classes.textError}>
              {formik.errors.email && formik.touched.email
                ? formik.errors.email
                : ""}
            </div>
            <FormSpacer space={32} />
            <div className={classes.buttonContainer}>
              <MyTypography
                className={classes.linkBack}
                onClick={onBack}
                variant="body3"
                data-testid="login-link"
              >
                <img src={chevronLeft} alt="Back to Sign In" />
                <FormattedMessage
                  defaultMessage="Back to Sign In"
                  description="description"
                />
              </MyTypography>
              <Button
                data-testid="submit"
                className={classNames(
                  classes.loginButton,
                  classes.resetPasswordSendButton
                )}
                disabled={disabled}
                variant="contained"
                color="secondary"
                type="submit"
              >
                <FormattedMessage
                  defaultMessage="Send"
                  description="password reset, button"
                />
              </Button>
            </div>
            <div className={classes.footerForgotPassword}>
              <MyTypography
                component="span"
                variant="caption"
                color="textSecondary"
                className={classes.description}
              >
                <FormattedMessage defaultMessage="In case you forgot your email or can't remember your username, please contact:" />
              </MyTypography>
              <MyTypography
                component="span"
                variant="caption"
                className={classes.bold}
              >
                {" "}
                (+66) 02-586-5555
              </MyTypography>
            </div>
          </>
        </form>
      </div>
    </div>
  );
};

ResetPasswordPage.displayName = "ResetPasswordPage";
export default ResetPasswordPage;
